import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import CountUp from "react-countup";
import { useTimelineContext } from "../contexts/TimelineContext";
import useSmoothScroll from "../hooks/useSmoothScroll";
import { useTheme } from "../hooks/useTheme";
import { HOVER_ANIMATIONS } from "../constants/animations";
import { BUTTON_STYLES } from "../constants/styles";
import InteractiveProfileImage from "./InteractiveProfileImage";

interface HeaderProps {
  onAnimationsComplete: () => void;
}

function Header({ onAnimationsComplete }: HeaderProps) {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="relative z-10">
      <HeaderContent
        theme={theme}
        toggleTheme={toggleTheme}
        onAnimationsComplete={onAnimationsComplete}
      />
    </div>
  );
}

interface HeaderContentProps {
  theme: string;
  toggleTheme: () => void;
  onAnimationsComplete: () => void;
}

// Animated Counter Component using react-countup
const AnimatedCounter = ({ end, delay = 500 }: { end: number; delay?: number }) => {
  const [startAnimation, setStartAnimation] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setStartAnimation(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  // Reserve space for the full number to prevent layout shifts
  const maxWidth = end.toLocaleString().length;
  return (
    <span
      style={{
        display: 'inline-block',
        minWidth: `${maxWidth * 0.62}em`,
        textAlign: 'center'
      }}
    >
      <CountUp
        //start={0}
        end={startAnimation ? end : 0}
        duration={4}
        separator=","
        suffix="+"
      />
    </span>
  );
};

function HeaderContent({ onAnimationsComplete }: HeaderContentProps) {
  const [showContent, setShowContent] = useState<boolean>(false);
  const [showButtons, setShowButtons] = useState<boolean>(false);
  const scrollToElement = useSmoothScroll();
  const { setActiveCategory } = useTimelineContext();

  useEffect(() => {
    const contentTimer = setTimeout(() => setShowContent(true), 500);
    const buttonsTimer = setTimeout(() => setShowButtons(true), 1500);
    const completeTimer = setTimeout(() => {
      onAnimationsComplete();
    }, 2000);

    return () => {
      clearTimeout(contentTimer);
      clearTimeout(buttonsTimer);
      clearTimeout(completeTimer);
    };
  }, [onAnimationsComplete]);

  const handleViewProjects = () => {
    scrollToElement("experience");
    setActiveCategory("projects");
  };

  const handleContactMe = () => {
    scrollToElement("contact");
  };

  return (
    <div className="duration-300 relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-20">
      <InteractiveProfileImage
        src={require("../images/profile/self.png")}
        backgroundSrc={require("../images/profile/background.png")}
        alt="Tomer Rosenfeld - Full Stack Developer"
        className="mb-12"
      />

      <div className="text-center max-w-5xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={showContent ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold dark:text-surface-50 text-accent-900 mb-8 leading-tight font-display"
        >
          The Developer Behind{" "}
          <span className="text-primary-500 dark:text-primary-400 font-extrabold">
            <AnimatedCounter end={20000000} />
          </span>{" "}
          Downloads
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={showContent ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-lg md:text-xl lg:text-2xl dark:text-accent-300 text-accent-600 mb-6 font-light"
        >
          Elite Software Engineer • Military Cyber Expert • Startup Founder
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={showContent ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="text-base md:text-lg dark:text-accent-400 text-accent-600 mb-12 max-w-3xl mx-auto leading-relaxed"
        >
          I build products that millions use, architecting scalable solutions from mobile apps to AI systems.
        </motion.p>
      </div>
      <motion.div
        className="flex flex-col sm:flex-row gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={showButtons ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <motion.div
          className="w-full sm:w-auto flex justify-center"
          animate={{
            y: [0, -1, 0],
            x: [0, -1, 0],
            rotateX: [0, 5.5, 0, -5.5, 0],
            rotateY: [0, 0.5, 0, -0.5, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <motion.button
            onClick={handleViewProjects}
            {...HOVER_ANIMATIONS.scale}
            className={`${BUTTON_STYLES.primary} w-full sm:w-auto`}
          >
            View My Work
          </motion.button>
        </motion.div>
        <motion.div
          className="w-full sm:w-auto flex justify-center"
          animate={{
            y: [0, -1.5, 0],
            x: [0, -0.5, 0],
            rotateX: [0, -5.5, 0, 9.5, 0],
            rotateY: [0, -0.5, 0, 0.5, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        >
          <motion.button
            onClick={handleContactMe}
            {...HOVER_ANIMATIONS.scale}
            className={`${BUTTON_STYLES.secondary} w-full sm:w-auto`}
          >
            Hire Me
          </motion.button>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default Header;
